/**
 * جوي — المساعد الذكيّ في النظام.
 *
 * مديرُ الشركة يسأله عن أرقامها، ويطلب منه تقاريرَ، ويكلّفه بمهامَّ في
 * مواعيدَ محدّدة، ويضع له قواعدَ يراقب بها العمل فينبّه حين يختلّ شيء.
 *
 * ## ثلاثةُ أشياءَ مختلفة لا واحد
 *
 *   المحادثة  سؤالٌ وجواب. النموذجُ يقرأ البيانات بأدواتٍ لا يكتب بها.
 *   المهمّة   سؤالٌ مؤجَّل: يُطرح على النموذج في موعده ويُنشر جوابُه.
 *   المراقبة  قاعدةٌ تُحسب في قاعدة البيانات كلّ بضع دقائق — بلا نموذج.
 *
 * والفرقُ بين الأخيرتين مقصود: فحصُ «من لم يسجّل حضوره» كلَّ عشر دقائق
 * بالنموذج كلفةٌ لا تنتهي وجوابٌ قد يتفاوت بين مرّةٍ وأخرى، والقاعدةُ
 * تجيب الجوابَ نفسه على البيانات نفسها. جوي يصوغ القاعدة من كلام المدير،
 * والشيفرةُ تحسبها.
 */

export const JOY_NAME = 'جوي';

/** الأدوار التي تحدّث جوي — مديرُ النظام، لأنّ أدواته تقرأ الشركةَ كلَّها. */
export const JOY_MIN_ROLE = 'ADMIN' as const;

/** أقصى أدوارٍ للنموذج في الطلب الواحد — حارسٌ على حلقةٍ لا تنتهي. */
export const JOY_MAX_TURNS = 12;

/** تشغيلٌ أقدمُ من هذا يُعدّ متوقّفًا — عمليةٌ سقطت في منتصفه. */
export const JOY_RUN_STALE_MS = 5 * 60_000;

export const JOY_MAX_MESSAGE_CHARS = 4000;

/* ==========================================================================
   الحالات
   ========================================================================== */

export const JOY_RUN_STATUSES = ['IDLE', 'RUNNING', 'FAILED'] as const;
export type JoyRunStatus = (typeof JOY_RUN_STATUSES)[number];

export const JOY_JOB_STATUSES = ['ACTIVE', 'PAUSED', 'DONE'] as const;
export type JoyJobStatus = (typeof JOY_JOB_STATUSES)[number];

export const JOY_JOB_STATUS_LABELS: Readonly<Record<JoyJobStatus, string>> = {
  ACTIVE: 'تعمل',
  PAUSED: 'موقوفة',
  DONE: 'انتهت',
};

/* ==========================================================================
   المواعيد
   ========================================================================== */

export const JOY_SCHEDULE_KINDS = ['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY'] as const;
export type JoyScheduleKind = (typeof JOY_SCHEDULE_KINDS)[number];

/** الأحد صفر — كما في `Date.getDay`. */
export const WEEKDAY_NAMES = [
  'الأحد',
  'الاثنين',
  'الثلاثاء',
  'الأربعاء',
  'الخميس',
  'الجمعة',
  'السبت',
] as const;

export interface JoySchedule {
  kind: JoyScheduleKind;
  /** «HH:mm» بتوقيت الشركة. */
  time: string;
  weekday?: number | null;
  /** حتى ٢٨: شهرٌ بلا يومٍ ثلاثين لا يُسقط المهمّة. */
  dayOfMonth?: number | null;
  /** «YYYY-MM-DD» للمرّة الواحدة. */
  date?: string | null;
}

export function describeSchedule(s: JoySchedule): string {
  switch (s.kind) {
    case 'ONCE':
      return `مرّةً واحدة ${s.date ?? ''} الساعة ${s.time}`;
    case 'DAILY':
      return `كلَّ يوم الساعة ${s.time}`;
    case 'WEEKLY':
      return `كلَّ ${WEEKDAY_NAMES[s.weekday ?? 0]} الساعة ${s.time}`;
    case 'MONTHLY':
      return `يوم ${s.dayOfMonth ?? 1} من كلّ شهر الساعة ${s.time}`;
  }
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** مكوّناتُ اللحظة كما تُقرأ على ساعة الشركة. */
export function zonedParts(at: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(at);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0';
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
  };
}

/** اليوم بتوقيت الشركة «YYYY-MM-DD» — مفتاحُ «نُبّه عنه اليوم». */
export function zonedDate(at: Date, timeZone: string): string {
  const p = zonedParts(at, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * اللحظةُ العالمية لساعةٍ على حائط الشركة.
 *
 * بمرورين لا بواحد: فرقُ التوقيت يُحسب عند التخمين الأوّل، ويوم تغيير
 * الساعة الصيفية يختلف الفرقُ بين التخمين والنتيجة — والمرورُ الثاني
 * يصحّحه. مصرُ تعمل بالتوقيت الصيفيّ، فهذا ليس احتياطًا نظريًّا.
 */
export function zonedToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let guess = wall;
  for (let i = 0; i < 2; i++) {
    const p = zonedParts(new Date(guess), timeZone);
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += wall - seen;
  }
  return new Date(guess);
}

export function parseTime(time: string): { hour: number; minute: number } | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  return m ? { hour: Number(m[1]), minute: Number(m[2]) } : null;
}

/**
 * الموعدُ التالي بعد `after`.
 *
 * فارغٌ للمرّة الواحدة التي فات موعدها: مهمّةٌ طُلبت لأمسِ لا تُنفَّذ
 * اليوم فجأةً على أنّها «متأخّرة».
 */
export function nextRunAt(s: JoySchedule, after: Date, timeZone: string): Date | null {
  const t = parseTime(s.time);
  if (!t) return null;

  if (s.kind === 'ONCE') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.date ?? '');
    if (!m) return null;
    const at = zonedToUtc(Number(m[1]), Number(m[2]), Number(m[3]), t.hour, t.minute, timeZone);
    return at > after ? at : null;
  }

  const today = zonedParts(after, timeZone);
  /* سنةٌ من الأيام تكفي لكلّ نوع: الشهريّةُ تجد يومها في أقلّ من شهرين. */
  for (let offset = 0; offset < 400; offset++) {
    const cal = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
    const y = cal.getUTCFullYear();
    const mo = cal.getUTCMonth() + 1;
    const d = cal.getUTCDate();
    const wd = cal.getUTCDay();

    if (s.kind === 'WEEKLY' && wd !== (s.weekday ?? 0)) continue;
    if (s.kind === 'MONTHLY' && d !== (s.dayOfMonth ?? 1)) continue;

    const at = zonedToUtc(y, mo, d, t.hour, t.minute, timeZone);
    if (at > after) return at;
  }
  return null;
}

/* ==========================================================================
   المراقبة
   ========================================================================== */

export const JOY_WATCH_KINDS = [
  'OVERDUE_ACTIVITIES',
  'NOT_CHECKED_IN',
  'IDLE_ONLINE',
  'STALE_LEADS',
  'STUCK_DEALS',
  'OVERDUE_INVOICES',
] as const;
export type JoyWatchKind = (typeof JOY_WATCH_KINDS)[number];

export interface JoyWatchParams {
  /** OVERDUE_ACTIVITIES، STALE_LEADS: من كم يبدأ الخلل. */
  threshold?: number;
  /** STALE_LEADS، STUCK_DEALS، OVERDUE_INVOICES: كم يومًا. */
  days?: number;
  /** IDLE_ONLINE: كم دقيقةً بلا نشاط. */
  minutes?: number;
  /** NOT_CHECKED_IN: الموعدُ الذي بعده يُعدّ غيابًا. */
  time?: string;
  /** NOT_CHECKED_IN: أيامُ العمل — الأحد صفر. */
  weekdays?: number[];
}

export interface JoyWatchKindDef {
  key: JoyWatchKind;
  label: string;
  /** ما تفحصه القاعدة — يُكتب للنموذج وللمدير معًا. */
  description: string;
  defaults: JoyWatchParams;
  /** يُعاد الفحصُ كلّ كم دقيقة. */
  everyMinutes: number;
}

export const JOY_WATCH_KIND_DEFS: readonly JoyWatchKindDef[] = [
  {
    key: 'OVERDUE_ACTIVITIES',
    label: 'متابعاتٌ متأخّرة',
    description: 'موظّفٌ عنده عددٌ من المتابعات والمهامّ فات موعدُها ولم تُنجز.',
    defaults: { threshold: 3 },
    everyMinutes: 60,
  },
  {
    key: 'NOT_CHECKED_IN',
    label: 'لم يسجّل حضوره',
    description: 'موظّفٌ نشط لم يسجّل حضوره بحلول موعدٍ محدّد في يوم عمل.',
    defaults: { time: '10:00', weekdays: [0, 1, 2, 3, 4] },
    everyMinutes: 10,
  },
  {
    key: 'IDLE_ONLINE',
    label: 'حاضرٌ بلا نشاط',
    description: 'موظّفٌ سجّل حضوره ولم ينصرف، ولا نشاطَ له في النظام منذ مدّة.',
    defaults: { minutes: 45 },
    everyMinutes: 10,
  },
  {
    key: 'STALE_LEADS',
    label: 'عملاءُ محتملون مهمَلون',
    description: 'عملاءُ محتملون مفتوحون لم يُتواصل معهم منذ أيام، مجمَّعين بمسؤولهم.',
    defaults: { days: 7, threshold: 1 },
    everyMinutes: 60,
  },
  {
    key: 'STUCK_DEALS',
    label: 'صفقاتٌ متوقّفة',
    description: 'صفقاتٌ مفتوحة لم تتحرّك منذ أيام، مجمَّعةً بمسؤولها.',
    defaults: { days: 14 },
    everyMinutes: 60,
  },
  {
    key: 'OVERDUE_INVOICES',
    label: 'فواتيرُ متأخّرةُ التحصيل',
    description: 'فواتيرُ عملاء تجاوزت موعد استحقاقها بأيام ولم تُسدَّد كاملة.',
    defaults: { days: 1 },
    everyMinutes: 60,
  },
] as const;

export const JOY_WATCH_KIND_MAP = Object.fromEntries(
  JOY_WATCH_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<JoyWatchKind, JoyWatchKindDef>>;

/** وصفُ القاعدة بمعاملاتها — «متابعاتٌ متأخّرة: ٣ فأكثر». */
export function describeWatch(kind: JoyWatchKind, params: JoyWatchParams): string {
  const p = { ...JOY_WATCH_KIND_MAP[kind].defaults, ...params };
  switch (kind) {
    case 'OVERDUE_ACTIVITIES':
      return `موظّفٌ عنده ${p.threshold} متابعاتٍ متأخّرة فأكثر`;
    case 'NOT_CHECKED_IN':
      return `لم يسجّل حضوره بحلول ${p.time} (${(p.weekdays ?? [])
        .map((d) => WEEKDAY_NAMES[d])
        .join('، ')})`;
    case 'IDLE_ONLINE':
      return `حاضرٌ بلا نشاطٍ منذ ${p.minutes} دقيقة`;
    case 'STALE_LEADS':
      return `عملاءُ محتملون بلا تواصلٍ منذ ${p.days} أيام`;
    case 'STUCK_DEALS':
      return `صفقاتٌ لم تتحرّك منذ ${p.days} يومًا`;
    case 'OVERDUE_INVOICES':
      return `فواتيرُ متأخّرةٌ عن استحقاقها ${p.days} يومًا فأكثر`;
  }
}

/** ما تجده القاعدة — سطرٌ لكلّ شخصٍ أو سجلّ. */
export interface JoyFinding {
  /** مفتاحُ «نُبّه عنه اليوم». */
  key: string;
  /** الموظّفُ المعنيّ — يُنبَّه إن طُلب. */
  userId: string | null;
  text: string;
}

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface JoyMessageView {
  id: string;
  kind: 'USER' | 'ASSISTANT' | 'NOTICE';
  text: string;
  source: string | null;
  createdAt: string;
}

export interface JoyThreadView {
  /** مفتاحُ النموذج مضبوطٌ على الخادم — بدونه لا يُسأل جوي أصلًا. */
  configured: boolean;
  status: JoyRunStatus;
  runStartedAt: string | null;
  lastError: string | null;
  messages: JoyMessageView[];
}

export interface JoyTaskView {
  id: string;
  title: string;
  prompt: string;
  schedule: JoySchedule;
  scheduleText: string;
  alertOnly: boolean;
  status: JoyJobStatus;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastOk: boolean | null;
  lastResult: string | null;
}

export interface JoyWatchView {
  id: string;
  kind: JoyWatchKind;
  label: string;
  text: string;
  params: JoyWatchParams;
  notifyEmployee: boolean;
  status: JoyJobStatus;
  lastCheckedAt: string | null;
  lastTriggeredAt: string | null;
}

export interface JoyJobsView {
  tasks: JoyTaskView[];
  watches: JoyWatchView[];
}
